import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import { useAdminAuth } from "@/features/admin/hooks/useAdminAuth";
import { AdminShell } from "@/features/admin/shell/AdminShell";
import { StatEntryTable } from "@/features/admin/games/components/StatEntryTable";
import { ImportBoxScoreModal } from "@/features/admin/games/components/ImportBoxScoreModal";
import { useAdminGame } from "@/features/admin/games/hooks/useAdminGame";
import type { StatLine } from "@/features/admin/games/lib/emptyStatLine";
import { Button } from "@/shared/ui/Button";
import { Skeleton } from "@/shared/ui/Skeleton";
import { useT } from "@/shared/i18n/LocaleContext";

export const Route = createFileRoute("/_adminGuarded/admin/games/$gameId")({
  component: AdminGameEntryPage,
});

function AdminGameEntryPage() {
  const t = useT();
  const { gameId } = Route.useParams();
  const { state } = useAdminAuth();
  const token = state.status === "authenticated" ? state.token : null;

  const data = useAdminGame(gameId as Id<"games">);

  const [importOpen, setImportOpen] = useState(false);
  const [prefill, setPrefill] = useState<Map<Id<"players">, StatLine> | null>(null);

  const breadcrumbs =
    data === undefined
      ? [{ label: t("admin.games.title"), to: "/admin/games" }, { label: "…" }]
      : [
          { label: t("admin.games.title"), to: "/admin/games" },
          {
            label: `${data.homeTeam?.shortName ?? "?"} @ ${data.awayTeam?.shortName ?? "?"}`,
          },
        ];

  return (
    <AdminShell breadcrumbs={breadcrumbs}>
      {data === undefined ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted">
              {t("admin.games.entry.eyebrow")}
            </p>
            {token && data.game.isEligible ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setImportOpen(true)}
              >
                {t("admin.games.import.openButton")}
              </Button>
            ) : null}
          </div>
          {token ? (
            <StatEntryTable
              data={data}
              token={token}
              prefillFromExternal={prefill}
              hasStats={
                Object.keys(data.existingStats).length > 0 ||
                Object.keys(data.existingPitcherStats).length > 0
              }
            />
          ) : null}

          {token ? (
            <ImportBoxScoreModal
              gameId={gameId as Id<"games">}
              token={token}
              open={importOpen}
              onClose={() => setImportOpen(false)}
              onApply={(p) => setPrefill(p)}
            />
          ) : null}
        </>
      )}
    </AdminShell>
  );
}
